/**
 * Early-join window: an event in Up Next becomes joinable this long
 * before its start time. Hardcoded for v1 — not configurable per tenant.
 */
const EARLY_JOIN_WINDOW_MS = 15 * 60 * 1000

/**
 * A stored meeting URL only ever reaches an `<a href>` if it is http(s).
 * The BE schema is the authoritative gate, but a bad value arriving from
 * a future ingress (import, direct SQL, a new write path) must never
 * render as a live `javascript:` / `data:` href — so every render site
 * mirrors the BE's protocol allowlist through this guard.
 */
export function isHttpUrl(value: string | null | undefined): value is string {
  if (!value) return false
  try {
    const { protocol } = new URL(value)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

export interface EventJoinableArgs {
  kind: string
  /**
   * The server-derived canonical column bucket (`'Today'` / `'Up Next'` /
   * `'Done'` / …). Lazy on-read coercion owns which column an event sits
   * in — this is never re-derived client-side from start/end.
   */
  columnName: string | null | undefined
  startAt: string | null | undefined
  meetingUrl: string | null | undefined
  now: number
}

/**
 * Whether an event card should surface its Join button. Pure so it stays
 * unit-testable. Only the ≤15-minute sub-window within Up Next is
 * computed client-side (from `startAt` + the ticker `now`); the column
 * itself is the server's authority.
 */
export function isEventJoinable({
  kind,
  columnName,
  startAt,
  meetingUrl,
  now,
}: EventJoinableArgs): boolean {
  if (kind !== 'event') return false
  if (!isHttpUrl(meetingUrl)) return false
  if (columnName === 'Today') return true
  if (columnName === 'Up Next') {
    if (!startAt) return false
    const start = new Date(startAt).getTime()
    if (Number.isNaN(start)) return false
    return start - now <= EARLY_JOIN_WINDOW_MS
  }
  return false
}
